/**
 * RemoteIDStatusModal.jsx - Wave 2B Item 5A
 * Modal for editing Aircraft Remote ID configuration.
 */

import React, { useState, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Wifi, Loader2, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const RemoteIDStatusModal = ({
  open,
  onOpenChange,
  aircraft,
  onSuccess,
}) => {
  const { t, language } = useLanguage();
  const tr = t.remoteId || {};
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    remote_id_type: "none",
    remote_id_serial: "",
    remote_id_exemption_id: "",
    remote_id_exemption_expires_at: "",
  });

  useEffect(() => {
    if (aircraft && open) {
      setFormData({
        remote_id_type: aircraft.remote_id_type || "none",
        remote_id_serial: aircraft.remote_id_serial || "",
        remote_id_exemption_id: aircraft.remote_id_exemption_id || "",
        remote_id_exemption_expires_at:
          aircraft.remote_id_exemption_expires_at?.split("T")[0] || "",
      });
    }
  }, [aircraft, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation for exemption
    if (
      formData.remote_id_type === "exempted" &&
      !formData.remote_id_exemption_id.trim()
    ) {
      toast.error(
        tr.exemptionIdRequired ||
          "Exemption ID is required when status is 'Exempted'"
      );
      return;
    }

    setLoading(true);

    try {
      // Build payload based on remote_id_type
      const payload = {
        remote_id_type: formData.remote_id_type,
        remote_id_serial: formData.remote_id_serial || null,
        remote_id_compliant: ["built_in", "add_on", "modular"].includes(
          formData.remote_id_type
        ),
      };

      // Add exemption fields only if exempted
      if (formData.remote_id_type === "exempted") {
        payload.remote_id_exemption_id = formData.remote_id_exemption_id;
        payload.remote_id_exemption_expires_at =
          formData.remote_id_exemption_expires_at || null;
      } else {
        payload.remote_id_exemption_id = null;
        payload.remote_id_exemption_expires_at = null;
      }

      await api.put(`/aircraft/${aircraft.id}`, payload);
      toast.success(tr.ridUpdated || "Remote ID configuration updated");
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg =
        language === "cs" ? detail?.message_cs : detail?.message_en || detail;
      toast.error(msg || (tr.saveFailed || "Failed to update RID configuration"));
    } finally {
      setLoading(false);
    }
  };

  const ridTypes = [
    {
      value: "built_in",
      label: tr.builtIn || "Built-in (Factory Integrated)",
      compliant: true,
    },
    {
      value: "add_on",
      label: tr.addOn || "Add-on Module",
      compliant: true,
    },
    {
      value: "modular",
      label: tr.modular || "Modular (External)",
      compliant: true,
    },
    {
      value: "broadcast_only",
      label: tr.broadcastOnly || "Broadcast Only",
      compliant: false,
    },
    {
      value: "exempted",
      label: tr.exempted || "Exempted (With Documentation)",
      compliant: false,
    },
    {
      value: "none",
      label: tr.none || "None / Not Equipped",
      compliant: false,
    },
  ];

  const selectedType = ridTypes.find((t) => t.value === formData.remote_id_type);
  const showExemptionFields = formData.remote_id_type === "exempted";
  const showSerialField = ["built_in", "add_on", "modular"].includes(
    formData.remote_id_type
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="rid-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wifi className="h-5 w-5" />
            {tr.title || "Remote ID Configuration"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Aircraft Info */}
          <div className="p-3 bg-slate-50 rounded-lg border">
            <p className="text-sm text-slate-600">
              {tr.aircraft || "Aircraft"}:{" "}
              <span className="font-medium">
                {aircraft?.model} ({aircraft?.serial_number})
              </span>
            </p>
            {aircraft?.registration_number && (
              <p className="text-sm text-slate-600">
                {t.fleet?.registrationNumber || "Registration"}:{" "}
                <span className="font-mono">{aircraft.registration_number}</span>
              </p>
            )}
          </div>

          {/* RID Type */}
          <div className="space-y-2">
            <Label>{tr.ridType || "Remote ID Type"} *</Label>
            <Select
              value={formData.remote_id_type}
              onValueChange={(value) =>
                setFormData({ ...formData, remote_id_type: value })
              }
            >
              <SelectTrigger data-testid="rid-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ridTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      {type.compliant ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : type.value === "exempted" ? (
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      )}
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Indicator */}
          {selectedType && (
            <div
              className={`p-3 rounded-lg border ${
                selectedType.compliant
                  ? "bg-green-50 border-green-200"
                  : selectedType.value === "exempted"
                  ? "bg-amber-50 border-amber-200"
                  : "bg-red-50 border-red-200"
              }`}
            >
              <div className="flex items-start gap-2">
                {selectedType.compliant ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertTriangle
                    className={`h-5 w-5 mt-0.5 ${
                      selectedType.value === "exempted"
                        ? "text-amber-600"
                        : "text-red-600"
                    }`}
                  />
                )}
                <div>
                  <p
                    className={`text-sm font-medium ${
                      selectedType.compliant
                        ? "text-green-700"
                        : selectedType.value === "exempted"
                        ? "text-amber-700"
                        : "text-red-700"
                    }`}
                  >
                    {selectedType.compliant
                      ? tr.compliantStatus || "RID Compliant"
                      : selectedType.value === "exempted"
                      ? tr.exemptedStatus || "Exempted (requires documentation)"
                      : tr.nonCompliantStatus || "Non-Compliant"}
                  </p>
                  <p
                    className={`text-xs ${
                      selectedType.compliant
                        ? "text-green-600"
                        : selectedType.value === "exempted"
                        ? "text-amber-600"
                        : "text-red-600"
                    }`}
                  >
                    {selectedType.compliant
                      ? tr.compliantHint ||
                        "This aircraft can be used for EU operations requiring RID."
                      : selectedType.value === "exempted"
                      ? tr.exemptedHint ||
                        "Provide exemption documentation ID below."
                      : tr.nonCompliantHint ||
                        "This aircraft cannot be used for operations requiring RID."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Serial Number (for compliant types) */}
          {showSerialField && (
            <div className="space-y-2">
              <Label htmlFor="rid_serial">
                {tr.ridSerial || "RID Module Serial Number"}
              </Label>
              <Input
                id="rid_serial"
                value={formData.remote_id_serial}
                onChange={(e) =>
                  setFormData({ ...formData, remote_id_serial: e.target.value })
                }
                placeholder="e.g. RID-2026-ABC123"
                data-testid="rid-serial"
              />
              <p className="text-xs text-slate-500">
                {tr.ridSerialHint ||
                  "Optional: Serial number of the RID module for traceability"}
              </p>
            </div>
          )}

          {/* Exemption Fields */}
          {showExemptionFields && (
            <>
              <div className="space-y-2">
                <Label htmlFor="exemption_id">
                  {tr.exemptionId || "Exemption ID"} *
                </Label>
                <Input
                  id="exemption_id"
                  value={formData.remote_id_exemption_id}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      remote_id_exemption_id: e.target.value,
                    })
                  }
                  placeholder="e.g. EX-2025-CZ-001"
                  data-testid="rid-exemption-id"
                />
                <p className="text-xs text-slate-500">
                  {tr.exemptionIdHint ||
                    "The official exemption certificate ID from national CAA"}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="exemption_expires">
                  {tr.exemptionExpires || "Exemption Expires At"}
                </Label>
                <Input
                  id="exemption_expires"
                  type="date"
                  value={formData.remote_id_exemption_expires_at}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      remote_id_exemption_expires_at: e.target.value,
                    })
                  }
                  data-testid="rid-exemption-expires"
                />
              </div>
            </>
          )}

          {/* EU 2019/947 Info */}
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5" />
              <p className="text-xs text-blue-700">
                {tr.euRegInfo ||
                  "EU Regulation 2019/947 requires Remote ID for most drone operations in EU airspace as of 2024. Aircraft without compliant RID cannot be assigned to missions in regulated airspace."}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t.actions?.cancel || "Cancel"}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-primary hover:bg-primary/90"
              data-testid="rid-submit-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t.actions?.save || "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RemoteIDStatusModal;
